import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import {
  Plane, LogOut, CalendarDays, Users, DollarSign, CalendarCheck,
  Trash2, Ban, CheckCircle2, Plus, Loader2,
} from "lucide-react";
import { api, formatSlotTime, formatAud } from "@/lib/api";
import AnalyticsPanel from "@/components/AnalyticsPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const STATUS_STYLES = {
  confirmed: "bg-emerald-100 text-emerald-800",
  pending_payment: "bg-amber-100 text-amber-800",
  cancelled: "bg-slate-200 text-slate-500",
};
const SLOT_STYLES = {
  available: "bg-sky-100 text-sky-800",
  booked: "bg-emerald-100 text-emerald-800",
  held: "bg-amber-100 text-amber-800",
  blocked: "bg-slate-200 text-slate-500",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("bookings");
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [slots, setSlots] = useState([]);
  const [newSlot, setNewSlot] = useState({ date: "", time: "" });
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState("");
  const [meetingLink, setMeetingLink] = useState("");
  const [savingLink, setSavingLink] = useState(false);

  const loadAll = async () => {
    const [s, b, sl, st] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/bookings"),
      api.get("/admin/slots"),
      api.get("/admin/settings"),
    ]);
    setStats(s.data);
    setBookings(b.data);
    setSlots(sl.data);
    setMeetingLink(st.data.meeting_link || "");
  };

  const handleSaveSettings = async () => {
    setSavingLink(true);
    try {
      const res = await api.put("/admin/settings", { meeting_link: meetingLink });
      setMeetingLink(res.data.meeting_link);
      toast.success("Video call link saved — it will be included in every confirmation email");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save link");
    } finally {
      setSavingLink(false);
    }
  };

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => {
        setUser(r.data);
        loadAll().catch(() => toast.error("Could not load dashboard data"));
      })
      .catch(() => navigate("/admin/login"));
  }, [navigate]);

  const handleLogout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin/login");
  };

  const handleCancelBooking = async (id) => {
    if (!window.confirm("Cancel this booking? The time slot will be released.")) return;
    try {
      await api.post(`/admin/bookings/${id}/cancel`);
      toast.success("Booking cancelled and slot released");
      loadAll();
    } catch {
      toast.error("Could not cancel booking");
    }
  };

  const handleAddSlot = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      await api.post("/admin/slots", newSlot);
      toast.success("Slot added");
      setNewSlot({ date: "", time: "" });
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add slot");
    } finally {
      setAdding(false);
    }
  };

  const handleToggleSlot = async (slot) => {
    const next = slot.status === "blocked" ? "available" : "blocked";
    try {
      await api.patch(`/admin/slots/${slot.id}`, { status: next });
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update slot");
    }
  };

  const handleDeleteSlot = async (id) => {
    try {
      await api.delete(`/admin/slots/${id}`);
      toast.success("Slot removed");
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete slot");
    }
  };

  const filteredBookings = useMemo(() => {
    const q = search.toLowerCase();
    return bookings.filter((b) =>
      !q || b.name.toLowerCase().includes(q) || b.email.toLowerCase().includes(q) || (b.session || "").toLowerCase().includes(q)
    );
  }, [bookings, search]);

  const slotsByDate = useMemo(() => {
    const map = {};
    slots.forEach((s) => {
      map[s.date] = map[s.date] || [];
      map[s.date].push(s);
    });
    return map;
  }, [slots]);

  if (!user) {
    return (
      <div className="min-h-screen navy-panel flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-sky-400" />
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-slate-50">
      <header className="navy-panel text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
              <Plane className="w-4 h-4 text-[#F59E0B]" />
            </span>
            <span className="font-display font-bold text-lg uppercase tracking-wide">Cabin Crew Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span data-testid="admin-user-email" className="text-sm text-slate-300 hidden sm:block">{user.email}</span>
            <Link to="/" data-testid="admin-view-site-link" className="text-sm text-slate-300 hover:text-white transition-colors duration-150">View site</Link>
            <button data-testid="admin-logout-btn" onClick={handleLogout} className="flex items-center gap-1.5 text-sm text-slate-300 hover:text-white transition-colors duration-150">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-10">
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Users, label: "Total bookings", value: stats.total_bookings, tid: "stat-total-bookings" },
              { icon: CalendarCheck, label: "Confirmed", value: stats.confirmed, tid: "stat-confirmed" },
              { icon: DollarSign, label: "Revenue", value: formatAud(stats.revenue_cents), tid: "stat-revenue" },
              { icon: CalendarDays, label: "Open slots", value: stats.available_slots, tid: "stat-open-slots" },
            ].map((c) => (
              <div key={c.label} data-testid={c.tid} className="rounded-xl bg-white border border-slate-200 p-5">
                <c.icon className="w-5 h-5 text-sky-600" />
                <div className="font-display text-3xl font-bold text-[#0A192F] mt-2">{c.value}</div>
                <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold mt-1">{c.label}</div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-8 flex gap-2">
          {["bookings", "availability", "analytics", "settings"].map((t) => (
            <button
              key={t}
              data-testid={`tab-${t}`}
              onClick={() => setTab(t)}
              className={`rounded-full px-5 py-2 text-sm font-semibold capitalize transition-colors duration-150 ${
                tab === t ? "bg-[#0A192F] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-sky-300"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === "bookings" && (
          <div className="mt-6 rounded-xl bg-white border border-slate-200 overflow-hidden">
            <div className="p-5 border-b border-slate-100">
              <Input data-testid="bookings-search" placeholder="Search by name, email or session..." value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-sm" />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="bookings-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-widest text-slate-400 border-b border-slate-100">
                    <th className="px-5 py-3 font-semibold">Candidate</th>
                    <th className="px-5 py-3 font-semibold">Session</th>
                    <th className="px-5 py-3 font-semibold">When</th>
                    <th className="px-5 py-3 font-semibold">Notes</th>
                    <th className="px-5 py-3 font-semibold">Status</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {filteredBookings.map((b) => (
                    <tr key={b.id} data-testid={`booking-row-${b.id}`} className="border-b border-slate-50 hover:bg-slate-50/60">
                      <td className="px-5 py-4">
                        <div className="font-semibold text-[#0A192F]">{b.name}</div>
                        <div className="text-xs text-slate-400">{b.email}{b.phone ? ` — ${b.phone}` : ""}</div>
                      </td>
                      <td className="px-5 py-4 text-slate-600">{b.session}</td>
                      <td className="px-5 py-4 text-slate-600 whitespace-nowrap">{format(parseISO(b.date), "d MMM yyyy")}<br /><span className="text-xs text-slate-400">{formatSlotTime(b.time)}</span></td>
                      <td className="px-5 py-4 text-slate-500 text-xs max-w-[200px] truncate">{b.notes || "—"}</td>
                      <td className="px-5 py-4">
                        <span data-testid={`booking-status-${b.id}`} className={`rounded-full px-3 py-1 text-xs font-semibold capitalize ${STATUS_STYLES[b.status] || "bg-slate-100"}`}>
                          {b.status.replace(/_/g, " ")}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-right">
                        {b.status !== "cancelled" && (
                          <button data-testid={`cancel-booking-${b.id}`} onClick={() => handleCancelBooking(b.id)} className="text-xs font-semibold text-red-500 hover:text-red-700 transition-colors duration-150">
                            Cancel
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {filteredBookings.length === 0 && (
                    <tr><td colSpan={6} data-testid="bookings-empty" className="px-5 py-12 text-center text-slate-400 text-sm">No bookings yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "availability" && (
          <div className="mt-6 space-y-6">
            <form onSubmit={handleAddSlot} className="rounded-xl bg-white border border-slate-200 p-5 flex flex-wrap items-end gap-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Date</label>
                <Input data-testid="new-slot-date" type="date" className="mt-1.5" value={newSlot.date} onChange={(e) => setNewSlot({ ...newSlot, date: e.target.value })} required />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Time (Sydney)</label>
                <Input data-testid="new-slot-time" type="time" className="mt-1.5" value={newSlot.time} onChange={(e) => setNewSlot({ ...newSlot, time: e.target.value })} required />
              </div>
              <Button data-testid="add-slot-btn" type="submit" disabled={adding} className="rounded-full bg-[#0A192F] hover:bg-sky-800">
                {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <span className="flex items-center gap-1.5"><Plus className="w-4 h-4" /> Add slot</span>}
              </Button>
            </form>

            <div className="space-y-4">
              {Object.entries(slotsByDate).map(([date, daySlots]) => (
                <div key={date} className="rounded-xl bg-white border border-slate-200 p-5">
                  <div className="font-display font-bold uppercase tracking-wide text-[#0A192F]">{format(parseISO(date), "EEEE, d MMMM yyyy")}</div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {daySlots.map((s) => (
                      <div key={s.id} data-testid={`slot-chip-${s.id}`} className={`flex items-center gap-2 rounded-full pl-4 pr-2 py-1.5 text-xs font-semibold ${SLOT_STYLES[s.status]}`}>
                        {formatSlotTime(s.time)} — {s.status}
                        {s.status === "available" && (
                          <button data-testid={`block-slot-${s.id}`} onClick={() => handleToggleSlot(s)} title="Block this slot" className="hover:text-slate-900 transition-colors duration-150"><Ban className="w-3.5 h-3.5" /></button>
                        )}
                        {s.status === "blocked" && (
                          <button data-testid={`unblock-slot-${s.id}`} onClick={() => handleToggleSlot(s)} title="Unblock this slot" className="hover:text-emerald-700 transition-colors duration-150"><CheckCircle2 className="w-3.5 h-3.5" /></button>
                        )}
                        {(s.status === "available" || s.status === "blocked") && (
                          <button data-testid={`delete-slot-${s.id}`} onClick={() => handleDeleteSlot(s.id)} title="Delete slot" className="hover:text-red-600 transition-colors duration-150"><Trash2 className="w-3.5 h-3.5" /></button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {slots.length === 0 && <div data-testid="slots-empty" className="rounded-xl bg-white border border-slate-200 p-12 text-center text-slate-400 text-sm">No upcoming slots. Add some above.</div>}
            </div>
          </div>
        )}

        {tab === "analytics" && <AnalyticsPanel />}

        {tab === "settings" && (
          <div className="mt-6 rounded-xl bg-white border border-slate-200 p-8 max-w-2xl" data-testid="settings-panel">
            <h3 className="font-semibold text-lg text-[#0A192F]">Video call link</h3>
            <p className="text-sm text-slate-500 mt-2 leading-relaxed">
              Paste your Google Meet or Zoom link below. It's automatically included in every booking confirmation email and shown on the customer's boarding pass. Leave it empty if you'd rather send links manually.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Input
                data-testid="meeting-link-input"
                placeholder="https://meet.google.com/xxx-xxxx-xxx"
                value={meetingLink}
                onChange={(e) => setMeetingLink(e.target.value)}
                className="flex-1 min-w-[260px]"
              />
              <Button data-testid="save-meeting-link-btn" onClick={handleSaveSettings} disabled={savingLink} className="rounded-full bg-[#0A192F] hover:bg-sky-800">
                {savingLink ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save link"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
